// MAWI OFFLINE+CLOUD OPERATIONS DATABASE SYSTEM
// Two separate databases for optimal performance and organization

// =============================================================================
// INTERFACES & TYPES
// =============================================================================

interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  isSynced?: boolean;
  syncStatus?: 'pending' | 'synced' | 'conflict' | 'error';
  lastSyncedAt?: string;
}

interface CloudProvider {
  id: string;
  name: string;
  type: 'openai' | 'anthropic' | 'google' | 'azure' | 'mawi' | 'custom';
  apiKey: string;
  baseUrl?: string;
  models: string[];
  settings: Record<string, any>;
  isActive: boolean;
}

// =============================================================================
// LLM DATABASE INTERFACES (mawiLLMdb)
// =============================================================================

interface ModelPreference extends BaseEntity {
  modelId: string;
  temperature: number;
  maxTokens: number;
  topP: number;
  presencePenalty: number;
  frequencyPenalty: number;
  systemMessage: string;
  lastUsed: string;
  isSelected: boolean;
}

interface LLMModel extends BaseEntity {
  name: string;
  provider: string;
  modelId: string;
  type: 'local' | 'cloud';
  size?: number;
  path?: string;
  downloadedAt?: string;
  isDownloaded: boolean;
  downloadProgress?: number;
  performance?: {
    tokensPerSecond?: number;
    memoryUsage?: number;
    contextLength?: number;
  };
  config?: Record<string, any>;
}

interface LLMCacheEntry {
  id: string;
  modelId: string;
  data: ArrayBuffer;
  metadata: {
    size: number;
    checksum: string;
    createdAt: string;
  };
}

interface LLMInferenceSession extends BaseEntity {
  modelId: string;
  sessionId: string;
  startTime: string;
  endTime?: string;
  totalTokens: number;
  cost?: number;
  status: 'active' | 'completed' | 'error';
}

// =============================================================================
// APP STORE DATABASE INTERFACES (mawidbStore)
// =============================================================================

interface UserAccount extends BaseEntity {
  email: string;
  username: string;
  avatar?: string;
  plan: 'free' | 'pro' | 'enterprise';
  subscription?: {
    id: string;
    status: string;
    expiresAt: string;
  };
}

interface UserProfile extends BaseEntity {
  userId: string;
  displayName: string;
  bio?: string;
  timezone: string;
  language: string;
  theme: 'light' | 'dark' | 'auto';
  notifications: {
    email: boolean;
    push: boolean;
    inApp: boolean;
  };
}

interface UserSettings extends BaseEntity {
  userId: string;
  key: string;
  value: any;
  category: string;
}

interface ChatHistory extends BaseEntity {
  userId: string;
  title: string;
  messages: ChatMessage[];
  modelId: string;
  totalTokens: number;
  cost?: number;
  tags?: string[];
  isArchived: boolean;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  tokens?: number;
  metadata?: Record<string, any>;
}

interface Project extends BaseEntity {
  name: string;
  description?: string;
  userId: string;
  teamId?: string;
  status: 'active' | 'archived' | 'completed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  tags?: string[];
  workspace?: ProjectWorkspace;
}

interface ProjectWorkspace {
  workingDirectory: string;
  environment: Record<string, any>;
  settings: Record<string, any>;
}

interface ProjectRules extends BaseEntity {
  projectId: string;
  name: string;
  description?: string;
  rules: Rule[];
  isActive: boolean;
}

interface Agent extends BaseEntity {
  name: string;
  role: string;
  description?: string;
  modelId: string;
  userId: string;
  teamId?: string;
  avatar?: string;
  status: 'active' | 'inactive' | 'training';
  capabilities: string[];
  settings: Record<string, any>;
  rules: AgentRules[];

  // Advanced AI features (Phase 3)
  memory?: {
    shortTerm: any[];
    longTerm: any[];
    workingMemory: any;
    maxShortTermSize: number;
    maxLongTermSize: number;
    retentionPolicy: 'aggressive' | 'moderate' | 'conservative';
  };

  learning?: {
    enabled: boolean;
    adaptationRate: number;
    learningGoals: any[];
    currentPerformance: any;
    improvementAreas: string[];
    trainingData: any[];
    modelVersions: any[];
  };

  personality?: {
    style: 'professional' | 'creative' | 'analytical' | 'supportive' | 'custom';
    tone: 'formal' | 'casual' | 'technical' | 'friendly';
    language: string;
    customTraits?: Record<string, any>;
  };

  performance?: {
    totalTasks: number;
    completedTasks: number;
    averageResponseTime: number;
    userSatisfaction: number;
    costEfficiency: number;
    errorRate: number;
  };
}

interface AgentRules extends BaseEntity {
  agentId: string;
  name: string;
  description?: string;
  rules: Rule[];
  priority: number;
  isActive: boolean;
}

interface Rule {
  id: string;
  type: 'condition' | 'action' | 'constraint';
  condition?: string;
  action?: string;
  parameters?: Record<string, any>;
  enabled: boolean;
}

interface Team extends BaseEntity {
  name: string;
  description?: string;
  userId: string;
  leaderAgentId: string;
  members: TeamMember[];
  rules: TeamRules[];
  status: 'active' | 'inactive';
  hierarchy: 'flat' | 'hierarchical';
}

interface TeamMember {
  agentId: string;
  role: string;
  order: number; // For hierarchical ordering
  permissions: string[];
  joinedAt: string;
}

interface TeamRules extends BaseEntity {
  teamId: string;
  name: string;
  description?: string;
  rules: Rule[];
  priority: number;
  isActive: boolean;
}

interface Task extends BaseEntity {
  title: string;
  description?: string;
  userId: string;
  projectId?: string;
  assignedTo: {
    type: 'agent' | 'team';
    id: string;
  };
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'failed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  scheduledAt?: string;
  recurrence?: TaskRecurrence;
  dependencies?: string[]; // Task IDs
  progress: number; // 0-100
  result?: any;
  error?: string;
}

interface TaskRecurrence {
  type: 'none' | 'daily' | 'weekly' | 'monthly' | 'custom';
  interval?: number;
  daysOfWeek?: number[];
  timeOfDay?: string;
  endDate?: string;
}

interface TaskRules extends BaseEntity {
  taskId: string;
  name: string;
  description?: string;
  rules: Rule[];
  isActive: boolean;
}

interface UserNotes extends BaseEntity {
  userId: string;
  title: string;
  content: string;
  tags?: string[];
  isPinned: boolean;
  category: string;
}

interface Knowledgebase extends BaseEntity {
  title: string;
  content: string;
  type: 'document' | 'snippet' | 'url' | 'note';
  tags?: string[];
  userId: string;
  isPublic: boolean;
  metadata: Record<string, any>;
  embeddings?: number[]; // For semantic search
}

interface ExecutionPlan extends BaseEntity {
  name: string;
  description?: string;
  targetId: string; // Project or Task ID
  targetType: 'project' | 'task';
  steps: ExecutionStep[];
  status: 'draft' | 'active' | 'completed' | 'failed';
  progress: number;
}

interface ExecutionStep {
  id: string;
  name: string;
  description?: string;
  type: 'task' | 'agent_action' | 'api_call' | 'file_operation' | 'wait';
  parameters: Record<string, any>;
  dependencies?: string[]; // Step IDs
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  result?: any;
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

interface TodoList extends BaseEntity {
  title: string;
  description?: string;
  targetId: string; // Project or Task ID
  targetType: 'project' | 'task';
  items: TodoItem[];
  status: 'active' | 'completed' | 'archived';
}

interface TodoItem {
  id: string;
  content: string;
  isCompleted: boolean;
  priority: 'low' | 'medium' | 'high';
  dueDate?: string;
  assignedTo?: string; // Agent ID
  createdAt: string;
  completedAt?: string;
}

// =============================================================================
// DATABASE SERVICES
// =============================================================================

class MawiLLMService {
  private dbName = 'mawiLLMdb';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  async initialize(): Promise<void> {
    await this.initDB();
  }

  private async initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Models table
        if (!db.objectStoreNames.contains('models')) {
          const modelsStore = db.createObjectStore('models', { keyPath: 'id' });
          modelsStore.createIndex('type', 'type');
          modelsStore.createIndex('provider', 'provider');
          modelsStore.createIndex('isDownloaded', 'isDownloaded');
        }

        // Model cache table
        if (!db.objectStoreNames.contains('model_cache')) {
          db.createObjectStore('model_cache', { keyPath: 'id' });
        }

        // Inference sessions table
        if (!db.objectStoreNames.contains('inference_sessions')) {
          const sessionsStore = db.createObjectStore('inference_sessions', { keyPath: 'id' });
          sessionsStore.createIndex('modelId', 'modelId');
          sessionsStore.createIndex('status', 'status');
          sessionsStore.createIndex('startTime', 'startTime');
        }

        // Model preferences table
        if (!db.objectStoreNames.contains('model_preferences')) {
          const preferencesStore = db.createObjectStore('model_preferences', { keyPath: 'id' });
          preferencesStore.createIndex('modelId', 'modelId', { unique: true });
          preferencesStore.createIndex('isSelected', 'isSelected');
        }

        // Cloud providers table
        if (!db.objectStoreNames.contains('cloud_providers')) {
          db.createObjectStore('cloud_providers', { keyPath: 'id' });
        }
      };
    });
  }

  // Model CRUD operations
  async createModel(model: Omit<LLMModel, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<LLMModel> {
    const db = await this.initDB();
    const transaction = db.transaction(['models'], 'readwrite');
    const store = transaction.objectStore('models');

    const newModel: LLMModel = {
      ...model,
      id: `model_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    };

    await new Promise((resolve, reject) => {
      const request = store.put(newModel);
      request.onsuccess = () => resolve(void 0);
      request.onerror = () => reject(request.error);
    });

    return newModel;
  }

  async getModels(): Promise<LLMModel[]> {
    const db = await this.initDB();
    const transaction = db.transaction(['models'], 'readonly');
    const store = transaction.objectStore('models');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getModel(id: string): Promise<LLMModel | null> {
    const db = await this.initDB();
    const transaction = db.transaction(['models'], 'readonly');
    const store = transaction.objectStore('models');

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async updateModel(id: string, updates: Partial<LLMModel>): Promise<LLMModel | null> {
    const existing = await this.getModel(id);
    if (!existing) return null;

    const updated: LLMModel = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString(),
      version: existing.version + 1,
    };

    const db = await this.initDB();
    const transaction = db.transaction(['models'], 'readwrite');
    const store = transaction.objectStore('models');

    await new Promise((resolve, reject) => {
      const request = store.put(updated);
      request.onsuccess = () => resolve(void 0);
      request.onerror = () => reject(request.error);
    });

    return updated;
  }

  async deleteModel(id: string): Promise<boolean> {
    const db = await this.initDB();
    const transaction = db.transaction(['models'], 'readwrite');
    const store = transaction.objectStore('models');

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // Cloud Provider CRUD
  async createCloudProvider(provider: Omit<CloudProvider, 'id'>): Promise<CloudProvider> {
    const db = await this.initDB();
    const transaction = db.transaction(['cloud_providers'], 'readwrite');
    const store = transaction.objectStore('cloud_providers');

    const newProvider: CloudProvider = {
      ...provider,
      id: `provider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    await new Promise((resolve, reject) => {
      const request = store.put(newProvider);
      request.onsuccess = () => resolve(void 0);
      request.onerror = () => reject(request.error);
    });

    return newProvider;
  }

  async getCloudProviders(): Promise<CloudProvider[]> {
    const db = await this.initDB();
    const transaction = db.transaction(['cloud_providers'], 'readonly');
    const store = transaction.objectStore('cloud_providers');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Inference Session management
  async startInferenceSession(modelId: string): Promise<LLMInferenceSession> {
    const db = await this.initDB();
    const transaction = db.transaction(['inference_sessions'], 'readwrite');
    const store = transaction.objectStore('inference_sessions');

    const session: LLMInferenceSession = {
      id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      modelId,
      sessionId: `sess_${Date.now()}`,
      startTime: new Date().toISOString(),
      totalTokens: 0,
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    };

    await new Promise((resolve, reject) => {
      const request = store.put(session);
      request.onsuccess = () => resolve(void 0);
      request.onerror = () => reject(request.error);
    });

    return session;
  }

  async updateInferenceSession(id: string, updates: Partial<LLMInferenceSession>): Promise<void> {
    const existing = await this.getInferenceSession(id);
    if (!existing) return;

    const updated: LLMInferenceSession = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString(),
      version: existing.version + 1,
    };

    const db = await this.initDB();
    const transaction = db.transaction(['inference_sessions'], 'readwrite');
    const store = transaction.objectStore('inference_sessions');

    await new Promise((resolve, reject) => {
      const request = store.put(updated);
      request.onsuccess = () => resolve(void 0);
      request.onerror = () => reject(request.error);
    });
  }

  async getInferenceSession(id: string): Promise<LLMInferenceSession | null> {
    const db = await this.initDB();
    const transaction = db.transaction(['inference_sessions'], 'readonly');
    const store = transaction.objectStore('inference_sessions');

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getActiveInferenceSessions(): Promise<LLMInferenceSession[]> {
    const db = await this.initDB();
    const transaction = db.transaction(['inference_sessions'], 'readonly');
    const store = transaction.objectStore('inference_sessions');
    const index = store.index('status');

    return new Promise((resolve, reject) => {
      const request = index.getAll('active');
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Model file storage (binary data)
  async setModelFile(modelId: string, fileData: ArrayBuffer, metadata: any): Promise<void> {
    const db = await this.initDB();
    const transaction = db.transaction(['model_cache'], 'readwrite');
    const store = transaction.objectStore('model_cache');

    const modelFile: LLMCacheEntry = {
      id: modelId,
      modelId,
      data: fileData,
      metadata: {
        size: fileData.byteLength,
        checksum: '', // Could implement checksum calculation
        createdAt: new Date().toISOString(),
        ...metadata
      }
    };

    return new Promise((resolve, reject) => {
      const request = store.put(modelFile);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getModelFile(modelId: string): Promise<LLMCacheEntry | null> {
    const db = await this.initDB();
    const transaction = db.transaction(['model_cache'], 'readonly');
    const store = transaction.objectStore('model_cache');

    return new Promise((resolve, reject) => {
      const request = store.get(modelId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  // Model Preferences CRUD operations
  async getModelPreferences(modelId: string): Promise<ModelPreference | null> {
    const db = await this.initDB();
    const transaction = db.transaction(['model_preferences'], 'readonly');
    const store = transaction.objectStore('model_preferences');
    const index = store.index('modelId');

    return new Promise((resolve, reject) => {
      const request = index.get(modelId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async createModelPreferences(preferences: Omit<ModelPreference, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<ModelPreference> {
    const db = await this.initDB();
    const transaction = db.transaction(['model_preferences'], 'readwrite');
    const store = transaction.objectStore('model_preferences');

    const newPreferences: ModelPreference = {
      ...preferences,
      id: `pref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    };

    await new Promise((resolve, reject) => {
      const request = store.put(newPreferences);
      request.onsuccess = () => resolve(void 0);
      request.onerror = () => reject(request.error);
    });

    return newPreferences;
  }

  async updateModelPreferences(modelId: string, updates: Partial<ModelPreference>): Promise<ModelPreference | null> {
    const existing = await this.getModelPreferences(modelId);
    if (!existing) return null;

    const updated: ModelPreference = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString(),
      version: existing.version + 1,
    };

    const db = await this.initDB();
    const transaction = db.transaction(['model_preferences'], 'readwrite');
    const store = transaction.objectStore('model_preferences');

    await new Promise((resolve, reject) => {
      const request = store.put(updated);
      request.onsuccess = () => resolve(void 0);
      request.onerror = () => reject(request.error);
    });

    return updated;
  }

  async getSelectedModelPreferences(): Promise<ModelPreference | null> {
    const db = await this.initDB();
    const transaction = db.transaction(['model_preferences'], 'readonly');
    const store = transaction.objectStore('model_preferences');
    const index = store.index('isSelected');

    return new Promise((resolve, reject) => {
      const request = index.get("true");
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get model preferences or create with defaults if not exists
   * This guarantees we always return values, never null
   */
  async getOrCreateModelPreferences(modelId: string, defaults: {
    temperature: number;
    maxTokens: number;
    topP: number;
    presencePenalty: number;
    frequencyPenalty: number;
    systemMessage: string;
  }): Promise<ModelPreference> {
    let preferences = await this.getModelPreferences(modelId);
    
    if (!preferences) {
      // No entry exists, create new one from defaults
      preferences = await this.createModelPreferences({
        modelId,
        ...defaults,
        lastUsed: new Date().toISOString(),
        isSelected: false
      });
      
      console.log(`Created new preferences entry for model ${modelId}`);
    } else {
      // Update last used timestamp
      await this.updateModelPreferences(modelId, {
        lastUsed: new Date().toISOString()
      });
    }
    
    return preferences;
  }
}

class MawiStoreService {
  private dbName = 'mawidbStore';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  async initialize(): Promise<void> {
    await this.initDB();
  }

  private async initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // User data tables
        if (!db.objectStoreNames.contains('user_accounts')) {
          const userStore = db.createObjectStore('user_accounts', { keyPath: 'id' });
          userStore.createIndex('email', 'email', { unique: true });
          userStore.createIndex('username', 'username', { unique: true });
        }

        if (!db.objectStoreNames.contains('user_profiles')) {
          const profileStore = db.createObjectStore('user_profiles', { keyPath: 'id' });
          profileStore.createIndex('userId', 'userId', { unique: true });
        }

        if (!db.objectStoreNames.contains('user_settings')) {
          const settingsStore = db.createObjectStore('user_settings', { keyPath: 'id' });
          settingsStore.createIndex('userId', 'userId');
          settingsStore.createIndex('category', 'category');
          settingsStore.createIndex('key', 'key');
        }

        // Chat and communication
        if (!db.objectStoreNames.contains('chat_history')) {
          const chatStore = db.createObjectStore('chat_history', { keyPath: 'id' });
          chatStore.createIndex('userId', 'userId');
          chatStore.createIndex('isArchived', 'isArchived');
          chatStore.createIndex('createdAt', 'createdAt');
        }

        // Projects and tasks
        if (!db.objectStoreNames.contains('projects')) {
          const projectStore = db.createObjectStore('projects', { keyPath: 'id' });
          projectStore.createIndex('userId', 'userId');
          projectStore.createIndex('teamId', 'teamId');
          projectStore.createIndex('status', 'status');
          projectStore.createIndex('priority', 'priority');
        }

        if (!db.objectStoreNames.contains('project_rules')) {
          const rulesStore = db.createObjectStore('project_rules', { keyPath: 'id' });
          rulesStore.createIndex('projectId', 'projectId');
        }

        if (!db.objectStoreNames.contains('tasks')) {
          const taskStore = db.createObjectStore('tasks', { keyPath: 'id' });
          taskStore.createIndex('userId', 'userId');
          taskStore.createIndex('projectId', 'projectId');
          taskStore.createIndex('status', 'status');
          taskStore.createIndex('priority', 'priority');
          taskStore.createIndex('scheduledAt', 'scheduledAt');
        }

        if (!db.objectStoreNames.contains('task_rules')) {
          const taskRulesStore = db.createObjectStore('task_rules', { keyPath: 'id' });
          taskRulesStore.createIndex('taskId', 'taskId');
        }

        // Agents and teams
        if (!db.objectStoreNames.contains('agents')) {
          const agentStore = db.createObjectStore('agents', { keyPath: 'id' });
          agentStore.createIndex('userId', 'userId');
          agentStore.createIndex('teamId', 'teamId');
          agentStore.createIndex('modelId', 'modelId');
          agentStore.createIndex('status', 'status');
        }

        if (!db.objectStoreNames.contains('agent_rules')) {
          const agentRulesStore = db.createObjectStore('agent_rules', { keyPath: 'id' });
          agentRulesStore.createIndex('agentId', 'agentId');
        }

        if (!db.objectStoreNames.contains('teams')) {
          const teamStore = db.createObjectStore('teams', { keyPath: 'id' });
          teamStore.createIndex('userId', 'userId');
          teamStore.createIndex('status', 'status');
        }

        if (!db.objectStoreNames.contains('team_rules')) {
          const teamRulesStore = db.createObjectStore('team_rules', { keyPath: 'id' });
          teamRulesStore.createIndex('teamId', 'teamId');
        }

        // Knowledge and content
        if (!db.objectStoreNames.contains('user_notes')) {
          const notesStore = db.createObjectStore('user_notes', { keyPath: 'id' });
          notesStore.createIndex('userId', 'userId');
          notesStore.createIndex('category', 'category');
          notesStore.createIndex('isPinned', 'isPinned');
        }

        if (!db.objectStoreNames.contains('knowledgebase')) {
          const kbStore = db.createObjectStore('knowledgebase', { keyPath: 'id' });
          kbStore.createIndex('userId', 'userId');
          kbStore.createIndex('type', 'type');
          kbStore.createIndex('isPublic', 'isPublic');
        }

        // Execution and planning
        if (!db.objectStoreNames.contains('execution_plans')) {
          const planStore = db.createObjectStore('execution_plans', { keyPath: 'id' });
          planStore.createIndex('targetId', 'targetId');
          planStore.createIndex('targetType', 'targetType');
          planStore.createIndex('status', 'status');
        }

        if (!db.objectStoreNames.contains('todo_lists')) {
          const todoStore = db.createObjectStore('todo_lists', { keyPath: 'id' });
          todoStore.createIndex('targetId', 'targetId');
          todoStore.createIndex('targetType', 'targetType');
          todoStore.createIndex('status', 'status');
        }
      };
    });
  }

  // Generic CRUD operations for all entities
  private async createEntity<T extends BaseEntity>(table: string, data: Omit<T, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<T> {
    const db = await this.initDB();
    const transaction = db.transaction([table], 'readwrite');
    const store = transaction.objectStore(table);

    const entity: T = {
      ...data,
      id: `${table.slice(0, -1)}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    } as T;

    await new Promise((resolve, reject) => {
      const request = store.put(entity);
      request.onsuccess = () => resolve(void 0);
      request.onerror = () => reject(request.error);
    });

    return entity;
  }

  private async getEntities<T>(table: string, index?: string, value?: any): Promise<T[]> {
    const db = await this.initDB();
    const transaction = db.transaction([table], 'readonly');
    const store = transaction.objectStore(table);

    return new Promise((resolve, reject) => {
      let request: IDBRequest;
      if (index && value !== undefined) {
        const idx = store.index(index);
        request = idx.getAll(value);
      } else {
        request = store.getAll();
      }
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async getEntity<T>(table: string, id: string): Promise<T | null> {
    const db = await this.initDB();
    const transaction = db.transaction([table], 'readonly');
    const store = transaction.objectStore(table);

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  private async updateEntity<T extends BaseEntity>(table: string, id: string, updates: Partial<T>): Promise<T | null> {
    const existing = await this.getEntity<T>(table, id);
    if (!existing) return null;

    const updated: T = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString(),
      version: existing.version + 1,
    };

    const db = await this.initDB();
    const transaction = db.transaction([table], 'readwrite');
    const store = transaction.objectStore(table);

    await new Promise((resolve, reject) => {
      const request = store.put(updated);
      request.onsuccess = () => resolve(void 0);
      request.onerror = () => reject(request.error);
    });

    return updated;
  }

  private async deleteEntity(table: string, id: string): Promise<boolean> {
    const db = await this.initDB();
    const transaction = db.transaction([table], 'readwrite');
    const store = transaction.objectStore(table);

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // User Account CRUD
  async createUserAccount(account: Omit<UserAccount, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<UserAccount> {
    return this.createEntity<UserAccount>('user_accounts', account);
  }

  async getUserAccounts(): Promise<UserAccount[]> {
    return this.getEntities<UserAccount>('user_accounts');
  }

  async getUserAccount(id: string): Promise<UserAccount | null> {
    return this.getEntity<UserAccount>('user_accounts', id);
  }

  async updateUserAccount(id: string, updates: Partial<UserAccount>): Promise<UserAccount | null> {
    return this.updateEntity<UserAccount>('user_accounts', id, updates);
  }

  async deleteUserAccount(id: string): Promise<boolean> {
    return this.deleteEntity('user_accounts', id);
  }

  // User Profile CRUD
  async createUserProfile(profile: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<UserProfile> {
    return this.createEntity<UserProfile>('user_profiles', profile);
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    const profiles = await this.getEntities<UserProfile>('user_profiles', 'userId', userId);
    return profiles[0] || null;
  }

  // User Settings CRUD
  async createUserSetting(setting: Omit<UserSettings, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<UserSettings> {
    return this.createEntity<UserSettings>('user_settings', setting);
  }

  async getUserSettings(userId: string, category?: string): Promise<UserSettings[]> {
    if (category) {
      return this.getEntities<UserSettings>('user_settings', 'userId', userId).then(settings =>
        settings.filter(s => s.category === category)
      );
    }
    return this.getEntities<UserSettings>('user_settings', 'userId', userId);
  }

  async updateUserSetting(id: string, updates: Partial<UserSettings>): Promise<UserSettings | null> {
    return this.updateEntity<UserSettings>('user_settings', id, updates);
  }

  async deleteUserSetting(id: string): Promise<boolean> {
    return this.deleteEntity('user_settings', id);
  }

  // Chat History CRUD
  async createChatHistory(chat: Omit<ChatHistory, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<ChatHistory> {
    return this.createEntity<ChatHistory>('chat_history', chat);
  }

  async getChatHistory(userId: string): Promise<ChatHistory[]> {
    return this.getEntities<ChatHistory>('chat_history', 'userId', userId);
  }

  async updateChatHistory(id: string, updates: Partial<ChatHistory>): Promise<ChatHistory | null> {
    return this.updateEntity<ChatHistory>('chat_history', id, updates);
  }

  async deleteChatHistory(id: string): Promise<boolean> {
    return this.deleteEntity('chat_history', id);
  }

  // Project CRUD
  async createProject(project: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<Project> {
    return this.createEntity<Project>('projects', project);
  }

  async getProjects(userId: string): Promise<Project[]> {
    return this.getEntities<Project>('projects', 'userId', userId);
  }

  async getProject(id: string): Promise<Project | null> {
    return this.getEntity<Project>('projects', id);
  }

  async updateProject(id: string, updates: Partial<Project>): Promise<Project | null> {
    return this.updateEntity<Project>('projects', id, updates);
  }

  async deleteProject(id: string): Promise<boolean> {
    return this.deleteEntity('projects', id);
  }

  // Agent CRUD
  async createAgent(agent: Omit<Agent, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<Agent> {
    return this.createEntity<Agent>('agents', agent);
  }

  async getAgents(userId: string): Promise<Agent[]> {
    return this.getEntities<Agent>('agents', 'userId', userId);
  }

  async getAgent(id: string): Promise<Agent | null> {
    return this.getEntity<Agent>('agents', id);
  }

  async updateAgent(id: string, updates: Partial<Agent>): Promise<Agent | null> {
    return this.updateEntity<Agent>('agents', id, updates);
  }

  async deleteAgent(id: string): Promise<boolean> {
    return this.deleteEntity('agents', id);
  }

  // Team CRUD
  async createTeam(team: Omit<Team, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<Team> {
    return this.createEntity<Team>('teams', team);
  }

  async getTeams(userId: string): Promise<Team[]> {
    return this.getEntities<Team>('teams', 'userId', userId);
  }

  async getTeam(id: string): Promise<Team | null> {
    return this.getEntity<Team>('teams', id);
  }

  async updateTeam(id: string, updates: Partial<Team>): Promise<Team | null> {
    return this.updateEntity<Team>('teams', id, updates);
  }

  async deleteTeam(id: string): Promise<boolean> {
    return this.deleteEntity('teams', id);
  }

  // Task CRUD
  async createTask(task: Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<Task> {
    return this.createEntity<Task>('tasks', task);
  }

  async getTasks(userId: string): Promise<Task[]> {
    return this.getEntities<Task>('tasks', 'userId', userId);
  }

  async getTask(id: string): Promise<Task | null> {
    return this.getEntity<Task>('tasks', id);
  }

  async updateTask(id: string, updates: Partial<Task>): Promise<Task | null> {
    return this.updateEntity<Task>('tasks', id, updates);
  }

  async deleteTask(id: string): Promise<boolean> {
    return this.deleteEntity('tasks', id);
  }

  // Knowledgebase CRUD
  async createKnowledgebaseEntry(entry: Omit<Knowledgebase, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<Knowledgebase> {
    return this.createEntity<Knowledgebase>('knowledgebase', entry);
  }

  async getKnowledgebaseEntries(userId: string): Promise<Knowledgebase[]> {
    return this.getEntities<Knowledgebase>('knowledgebase', 'userId', userId);
  }

  async searchKnowledgebase(query: string, userId: string): Promise<Knowledgebase[]> {
    const entries = await this.getKnowledgebaseEntries(userId);
    // Basic text search - can be enhanced with embeddings later
    return entries.filter(entry =>
      entry.title.toLowerCase().includes(query.toLowerCase()) ||
      entry.content.toLowerCase().includes(query.toLowerCase()) ||
      (entry.tags && entry.tags.some(tag => tag.toLowerCase().includes(query.toLowerCase())))
    );
  }

  async updateKnowledgebaseEntry(id: string, updates: Partial<Knowledgebase>): Promise<Knowledgebase | null> {
    return this.updateEntity<Knowledgebase>('knowledgebase', id, updates);
  }

  async deleteKnowledgebaseEntry(id: string): Promise<boolean> {
    return this.deleteEntity('knowledgebase', id);
  }

  // Execution Plans CRUD
  async createExecutionPlan(plan: Omit<ExecutionPlan, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<ExecutionPlan> {
    return this.createEntity<ExecutionPlan>('execution_plans', plan);
  }

  async getExecutionPlans(targetId: string, targetType: 'project' | 'task'): Promise<ExecutionPlan[]> {
    const plans = await this.getEntities<ExecutionPlan>('execution_plans', 'targetId', targetId);
    return plans.filter(plan => plan.targetType === targetType);
  }

  async updateExecutionPlan(id: string, updates: Partial<ExecutionPlan>): Promise<ExecutionPlan | null> {
    return this.updateEntity<ExecutionPlan>('execution_plans', id, updates);
  }

  async deleteExecutionPlan(id: string): Promise<boolean> {
    return this.deleteEntity('execution_plans', id);
  }

  // Todo Lists CRUD
  async createTodoList(list: Omit<TodoList, 'id' | 'createdAt' | 'updatedAt' | 'version'>): Promise<TodoList> {
    return this.createEntity<TodoList>('todo_lists', list);
  }

  async getTodoLists(targetId: string, targetType: 'project' | 'task'): Promise<TodoList[]> {
    const lists = await this.getEntities<TodoList>('todo_lists', 'targetId', targetId);
    return lists.filter(list => list.targetType === targetType);
  }

  async updateTodoList(id: string, updates: Partial<TodoList>): Promise<TodoList | null> {
    return this.updateEntity<TodoList>('todo_lists', id, updates);
  }

  async deleteTodoList(id: string): Promise<boolean> {
    return this.deleteEntity('todo_lists', id);
  }
}

// =============================================================================
// UNIFIED DATABASE MANAGER
// =============================================================================

class MawiDatabaseManager {
  private llmService: MawiLLMService;
  private storeService: MawiStoreService;
  private initialized = false;

  constructor() {
    this.llmService = new MawiLLMService();
    this.storeService = new MawiStoreService();
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // DO NOT DELETE existing databases - preserve user data and downloaded models
      // await this.deleteExistingDatabases();

      // Initialize new databases
      await Promise.all([
        this.llmService.initialize(),
        this.storeService.initialize()
      ]);

      this.initialized = true;
      console.log('MAWI Database Manager initialized successfully');
    } catch (error) {
      console.error('Failed to initialize MAWI Database Manager:', error);
      throw error;
    }
  }

  private async deleteExistingDatabases(): Promise<void> {
    const databasesToDelete = ['MawiUnifiedDB', 'mawiLLMdb', 'mawidbStore'];

    for (const dbName of databasesToDelete) {
      try {
        const deleteRequest = indexedDB.deleteDatabase(dbName);
        await new Promise((resolve, reject) => {
          deleteRequest.onsuccess = () => resolve(void 0);
          deleteRequest.onerror = () => reject(deleteRequest.error);
          deleteRequest.onblocked = () => {
            console.warn(`Delete database ${dbName} blocked, will retry`);
            setTimeout(() => resolve(void 0), 1000);
          };
        });
        console.log(`Deleted existing database: ${dbName}`);
      } catch (error) {
        console.warn(`Could not delete database ${dbName}:`, error);
      }
    }
  }

  // LLM Operations
  get llm() {
    return this.llmService;
  }

  // Store Operations
  get store() {
    return this.storeService;
  }

  // Backward compatibility methods (deprecated - use llm/store services)
  async setItem(key: string, data: any, type: string = 'general'): Promise<void> {
    console.warn('setItem is deprecated, use mawiDB.store.create* methods');
    // Route to appropriate service based on type
    if (type.includes('model') || type.includes('llm')) {
      // This is a temporary compatibility layer
      await this.llmService.createModel(data);
    } else {
      // Store in general storage - this would need to be implemented
      console.error('General storage not implemented in new architecture');
    }
  }

  async getItem(key: string): Promise<any> {
    console.warn('getItem is deprecated, use mawiDB.store.get* methods');
    // Try LLM service first, then store service
    const llmResult = await this.llmService.getModel(key.replace('model_', ''));
    if (llmResult) return { data: llmResult };
    return null;
  }

  async removeItem(key: string): Promise<void> {
    console.warn('removeItem is deprecated, use mawiDB.store.delete* methods');
    await this.llmService.deleteModel(key.replace('model_', ''));
  }

  async getUserDataByType(type: string) {
    console.warn('getUserDataByType is deprecated, use appropriate service methods');
    if (type === 'model_metadata') {
      const models = await this.llmService.getModels();
      return models.map(model => ({ data: model }));
    }
    return [];
  }

  async addUserData(data: any) {
    console.warn('addUserData is deprecated, use mawiDB.store.create* methods');
    // Route based on data type
    if (data.type === 'model_metadata') {
      await this.llmService.createModel(data.data);
    } else {
      console.error('addUserData not fully implemented for new architecture');
    }
  }

  async getUserData(id: string) {
    console.warn('getUserData is deprecated, use mawiDB.store.get* methods');
    return await this.getItem(id);
  }

  async updateUserData(data: any) {
    console.warn('updateUserData is deprecated, use mawiDB.store.update* methods');
    if (data.type === 'model_metadata') {
      await this.llmService.updateModel(data.id, data.data);
    } else {
      console.error('updateUserData not fully implemented for new architecture');
    }
  }

  async deleteUserData(id: string) {
    console.warn('deleteUserData is deprecated, use mawiDB.store.delete* methods');
    await this.removeItem(id);
  }

  // Sync methods
  async getAllChats() {
    return await this.storeService.getChatHistory('anonymous');
  }

  async saveChat(chat: any) {
    const existing = await this.storeService.getChatHistory('anonymous').then(chats => chats.find(c => c.id === chat.id));
    if (existing) {
      return await this.storeService.updateChatHistory(chat.id, chat);
    } else {
      return await this.storeService.createChatHistory(chat);
    }
  }

  async getAllKnowledge() {
    return await this.storeService.getKnowledgebaseEntries('anonymous');
  }

  async saveKnowledge(knowledge: any) {
    const existing = await this.storeService.getKnowledgebaseEntries('anonymous').then(entries => entries.find(e => e.id === knowledge.id));
    if (existing) {
      return await this.storeService.updateKnowledgebaseEntry(knowledge.id, knowledge);
    } else {
      return await this.storeService.createKnowledgebaseEntry(knowledge);
    }
  }

  async getUserSettings() {
    return await this.storeService.getUserSettings('anonymous');
  }

  async updateSettings(settings: any) {
    // Assuming settings has id
    return await this.storeService.updateUserSetting(settings.id, settings);
  }

  // Utility methods
  async clearAllData(): Promise<void> {
    await this.deleteExistingDatabases();
    this.initialized = false;
    await this.initialize();
  }

  async exportData(): Promise<string> {
    // Export all data as JSON for backup/sync
    const data = {
      llm: {
        models: await this.llmService.getModels(),
        providers: await this.llmService.getCloudProviders(),
      },
      store: {
        // Export key data (implement as needed)
      },
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    return JSON.stringify(data, null, 2);
  }

  async importData(jsonData: string): Promise<void> {
    const data = JSON.parse(jsonData);
    // Implement import logic (would be complex, depends on requirements)
    console.log('Data import not yet implemented');
  }
}

// Import sync service
import { SyncService } from './syncService';

// Export singleton instance
const mawiDB = new MawiDatabaseManager();
export default mawiDB;

// Export sync service factory
export const createSyncService = (serverUrl: string, authToken: string) => {
  return new SyncService();
};